import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material";
import FormDropdown from "../modals/FormDropdown";
import { ErrorBox, OrgModal, PrimaryButton, TextButton } from "./OrgModal";
import { fetchMembers, MEMBER_STATUS, orgErrorMessage, roleLabel, transferOwnership } from "../../utils/organisationApi";

// Ownership moves only to an existing active member; the old Owner becomes Admin (spec 4)
const TransferOwnershipModal = ({ isOpen, onClose, onTransferred, currentUserId }) => {
  const theme = useTheme();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedId("");
    setConfirmed(false);
    setError("");
    setLoading(true);
    fetchMembers({ status: MEMBER_STATUS.ACTIVE })
      .then(({ members: list }) =>
        setMembers(list.filter((member) => member.type === "member" && member.userId !== currentUserId && member.role !== "owner"))
      )
      .catch((err) => setError(orgErrorMessage(err, "Could not load members.")))
      .finally(() => setLoading(false));
  }, [isOpen, currentUserId]);

  const selected = members.find((member) => member.userId === selectedId);

  const handleTransfer = async () => {
    if (!selected) return;
    setSaving(true);
    setError("");
    try {
      await transferOwnership(selected.userId);
      onTransferred(selected);
      onClose();
    } catch (err) {
      setError(orgErrorMessage(err, "Could not transfer ownership."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <OrgModal
      isOpen={isOpen}
      onClose={saving ? () => {} : onClose}
      titleId="transfer-ownership-title"
      title="Transfer Ownership"
      footer={
        <>
          <TextButton onClick={onClose} disabled={saving}>
            Cancel
          </TextButton>
          <PrimaryButton onClick={handleTransfer} disabled={saving || !selected || !confirmed}>
            {saving ? "Transferring..." : "Transfer Ownership"}
          </PrimaryButton>
        </>
      }
    >
      <p className="text-sm">
        The new Owner gets the plan, the resume limit, Admin management and the right to delete the organisation.
        You'll become an Admin.
      </p>
      {loading ? (
        <p className="text-sm text-accent-gray-light">Loading members...</p>
      ) : members.length === 0 ? (
        <p className="text-sm p-3 rounded" style={{ backgroundColor: theme.palette.secondary.v5 }}>
          There's no one to transfer to yet. Invite a member and wait for them to accept first.
        </p>
      ) : (
        <div>
          <label htmlFor="transfer-member" className="block text-sm font-medium mb-2">
            New Owner <span className="text-red-500">*</span>
          </label>
          <FormDropdown
            id="transfer-member"
            searchable
            value={selectedId}
            placeholder="Choose a member"
            onChange={setSelectedId}
            options={members.map((member) => ({
              value: member.userId,
              label: `${member.name || member.email}${member.email && member.name ? ` · ${member.email}` : ""} (${roleLabel(member.role)})`,
              valueLabel: member.name || member.email,
            }))}
          />
        </div>
      )}
      {selected && (
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-1" />
          <span>
            I understand that <span className="font-semibold">{selected.name || selected.email}</span> becomes the Owner and I
            can't undo this myself.
          </span>
        </label>
      )}
      <ErrorBox>{error}</ErrorBox>
    </OrgModal>
  );
};

export default TransferOwnershipModal;
